import React, { useState, ChangeEvent, FocusEvent, FormEvent, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@fluentui/react-components";
import { Spinner } from "@fluentui/react";
import { RootState, AppDispatch } from "../../../state/store";
import TextField from "@mui/material/TextField";
import FormHelperText from "@mui/material/FormHelperText";

const useClasses = makeStyles({
    formContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 px",
        backgroundColor: "#fff",
        width: "100%",
        maxWidth: "500px",
        margin: "0 auto",
        fontFamily: "SogSegoe UI"
    },
    profileForm: { width: "100%", display: "flex", flexDirection: "column", gap: "2px" },
    flexRow: { display: "flex", justifyContent: "space-between", gap: "10px" },
    formField: { position: "relative", width: "100%", display: "flex", flexDirection: "column", marginBottom: "1px" },
    submitButtonEnabled: {
        backgroundColor: "#103699",
        color: "#fff",
        padding: "5px",
        border: "none",
        borderRadius: "6px",
        fontSize: "14px",
        cursor: "pointer",
        textAlign: "center",
        marginTop: "15px",
        "&:hover": { backgroundColor: "#2156e1" }
    },
    submitButtonDisabled: {
        backgroundColor: "#ccc",
        color: "#666",
        padding: "5px",
        border: "none",
        borderRadius: "6px",
        fontSize: "14px",
        cursor: "not-allowed",
        textAlign: "center",
        marginTop: "15px"
    },
    subTitle: { textAlign: "center", fontFamily: "Poppins", marginBottom: "3px", color: "#103699", fontSize: "16px" },
    error: { color: "red", textAlign: "center", paddingTop: "15px" },
    helperText: { color: "#B10E1C", fontSize: "12px", marginTop: "2px" },
    text: { color: "#ffffff", fontFamily: "Poppins", fontSize: "16px", fontWeight: "500", lineHeight: "22px", margin: "1px 0px" }
});

export interface UserProfileFormProps {
    onFormSubmit: (e: FormEvent<HTMLFormElement>) => void;
    isProfileUpdate: boolean;
    isSaving: boolean;
    profileErrorMessage: string | null;
}

const UserProfileForm: React.FC<UserProfileFormProps> = ({ onFormSubmit, isProfileUpdate, isSaving, profileErrorMessage }) => {
    const classes = useClasses();
    const dispatch = useDispatch<AppDispatch>();
    const userProfile = useSelector((state: RootState) => state.userProfiles.userProfile);

    const [formData, setFormData] = useState({ firstName: "", lastName: "", companyName: "", companydesc: "", industry: "", department: "", role: "" });
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    const [isChecked, setIsChecked] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);

    useEffect(() => {
        if (!hasInitialized && userProfile) {
            setFormData({
                firstName: userProfile.firstName || "",
                lastName: userProfile.lastName || "",
                companyName: userProfile.companyName || "",
                companydesc: userProfile.companydesc || "",
                industry: userProfile.industry || "",
                department: userProfile.department || "",
                role: userProfile.role || ""
            });
            if (isProfileUpdate) setIsChecked(true);
            setHasInitialized(true);
        }
    }, [userProfile, hasInitialized]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFormErrors(prev => ({ ...prev, [name]: "" }));
    };

    const validateField = (name: string, value: string): string => {
        const sanitized = value.replace(/['";]/g, "");
        if (value.trim() === "") return "This field is required.";
        if (/^[-\s]+$/.test(value)) return "Invalid input.";
        if (!/[a-zA-Z]/.test(sanitized)) return "Invalid input.";
        if (name === "companydesc") {
            if (sanitized.length < 10) return "Company description must be at least 10 characters long.";
            if (sanitized.length > 450) return "Company description must not exceed 450 characters.";
        } else if (sanitized.length > 50) return "Cannot exceed 50 characters.";
        return "";
    };

    const handleInputBlur = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        setFormErrors(prev => ({ ...prev, [name]: error }));
    };

    const handleInputFocus = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name } = e.target;
        setFormErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const newErrors: { [key: string]: string } = {};
        Object.entries(formData).forEach(([name, value]) => {
            const err = validateField(name, value);
            if (err) newErrors[name] = err;
        });
        setFormErrors(newErrors);
        if (Object.keys(newErrors).length === 0) onFormSubmit(e);
    };

    const isFormValid = () => {
        return Object.entries(formData).every(([key, val]) => validateField(key, val) === "");
    };

    return (
        <div className={classes.formContainer}>
            <p className={classes.subTitle}>
                {isProfileUpdate
                    ? "Keep your profile up-to-date to help our AI provide tailored results."
                    : "In order to get started, we need a little information about you."}
            </p>
            <form className={classes.profileForm} onSubmit={handleFormSubmit} noValidate>
                {Object.entries(formData).map(([field, value]) => (
                    <div key={field} className={classes.formField}>
                        <TextField
                            size="small"
                            id={field}
                            name={field}
                            label={
                                field === "companydesc" ? "Company Description" : field.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, s => s.toUpperCase())
                            }
                            value={value}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                            onFocus={handleInputFocus}
                            multiline={field === "companydesc"}
                            maxRows={field === "companydesc" ? 2 : undefined}
                            error={Boolean(formErrors[field])}
                            helperText={formErrors[field] || " "}
                            sx={{
                                "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#103699" },
                                "& .MuiInputLabel-root": { color: "#B0B0B0" },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#103699" },
                                "& .MuiInputBase-input": { fontFamily: "Poppins, sans-serif", color: "#103699" }
                            }}
                        />
                    </div>
                ))}

                {profileErrorMessage ? (
                    <div className={classes.error}>{profileErrorMessage}</div>
                ) : isSaving ? (
                    <div>
                        <Spinner label="Saving..." />
                    </div>
                ) : (
                    <button type="submit" className={isFormValid() ? classes.submitButtonEnabled : classes.submitButtonDisabled} disabled={!isFormValid()}>
                        <div className={classes.text}>{isProfileUpdate ? "Update Profile" : "Continue"}</div>
                    </button>
                )}
            </form>
        </div>
    );
};

export default UserProfileForm;
